/**
 * HorizonSection.tsx
 * Sección «Horizonte 2026» — roadmap de próximas funcionalidades
 *
 * @version 2.0.0
 * @description Timeline vertical con pista teal lateral, cabecera split y
 *   etiquetas de estado por módulo. Variante B (Timeline Roadmap) aprobada
 *   el 2026-04-03. DS v2 (clases horizon-*).
 */

import { type FC, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

// ============================================
// TYPES
// ============================================

interface HorizonItem {
  id: string;
  code: string;
  category: string;
  title: string;
  desc: string;
  chips: string[];
  statusLabel: string;
  /** 'planned' | 'exploration' */
  statusVariant: 'planned' | 'exploration';
  icon: ReactNode;
}

// ============================================
// DATA
// ============================================

const HORIZON_ITEMS: HorizonItem[] = [
  {
    id: 'f5-riesgo',
    code: 'F5',
    category: 'Análisis de riesgo',
    title: 'Análisis territorial de riesgo',
    desc: 'Cruce de infraestructuras con zonas de riesgo: inundaciones SNCZI, incendios INFOCA, instalaciones SEVESO e isócronas de evacuación.',
    chips: ['Buffers SEVESO', 'SNCZI', 'INFOCA', 'Isócronas evacuación'],
    statusLabel: 'En hoja de ruta',
    statusVariant: 'planned',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  },
  {
    id: 'f6-cobertura',
    code: 'F6',
    category: 'Cobertura',
    title: 'Cobertura de emergencias',
    desc: 'Isócronas de respuesta de hospitales, bomberos y policía. Detección de huecos de cobertura por municipio mediante ORS.',
    chips: ['ORS Isócronas', 'Hospitales', 'Bomberos', 'Huecos cobertura'],
    statusLabel: 'En hoja de ruta',
    statusVariant: 'planned',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    id: 'f7-alertas',
    code: 'F7',
    category: 'Alertas',
    title: 'Alertas en tiempo real',
    desc: 'Integración con AEMET, niveles SAIH, focos activos EFFIS/INFOCA y panel operativo para el CECEM.',
    chips: ['AEMET', 'SAIH', 'EFFIS/INFOCA', 'Panel CECEM'],
    statusLabel: 'En hoja de ruta',
    statusVariant: 'planned',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
        <line x1="6" y1="1" x2="6" y2="4"/>
        <line x1="10" y1="1" x2="10" y2="4"/>
        <line x1="14" y1="1" x2="14" y2="4"/>
      </svg>
    ),
  },
  {
    id: 'f8-ia',
    code: 'F8',
    category: 'IA',
    title: 'Consultas en lenguaje natural',
    desc: '«¿Qué colegios de Guadix están a menos de 200 m de un cauce inundable?» → PostGIS + Claude MCP.',
    chips: ['PostGIS', 'Claude MCP', 'SQL natural', 'Mapa respuesta'],
    statusLabel: 'Exploración',
    statusVariant: 'exploration',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
];

// ============================================
// COMPONENT
// ============================================

export const HorizonSection: FC = () => {
  return (
    <section className="horizon-section" id="roadmap">
      <div className="horizon-inner">

        {/* Cabecera split */}
        <div className="horizon-header">
          <div className="horizon-header-left">
            <div className="horizon-eyebrow">
              <span className="horizon-eyebrow-pill">
                <span className="horizon-eyebrow-dot" />
                En desarrollo · Horizonte 2026
              </span>
            </div>
            <h2 className="horizon-title">Próximas funcionalidades</h2>
            <p className="horizon-subtitle">
              Módulos en desarrollo para ampliar la cobertura funcional de PTEL Tool en Andalucía.
            </p>
          </div>

        </div>

        {/* Timeline */}
        <div className="horizon-timeline">
          {HORIZON_ITEMS.map((item) => (
            <div key={item.id} className="horizon-tl-item">

              {/* Nodo de la pista */}
              <div className="horizon-tl-node" aria-hidden="true" />

              {/* Tarjeta */}
              <div className="horizon-tl-card">
                <div className="horizon-tl-card-main">
                  <div className="horizon-tl-meta">
                    <span className="horizon-tl-code">{item.code}</span>
                    <span className="horizon-tl-category">{item.category}</span>
                  </div>
                  <div className="horizon-tl-title">{item.title}</div>
                  <div className="horizon-tl-desc">{item.desc}</div>
                  <div className="horizon-card-chips">
                    {item.chips.map((chip) => (
                      <span key={chip} className="horizon-chip">{chip}</span>
                    ))}
                  </div>
                </div>
                <div className="horizon-tl-card-aside">
                  <span
                    className={`horizon-tl-status horizon-tl-status--${item.statusVariant}`}
                  >
                    {item.statusLabel}
                  </span>
                </div>
              </div>

            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="horizon-cta">
          <div className="horizon-cta-text">
            Sigue el avance del proyecto en la hoja de ruta pública
          </div>
          <Link to="/roadmap" className="horizon-cta-link">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            Ver hoja de ruta
          </Link>
        </div>

      </div>
    </section>
  );
};
